// MessageInput.jsx
import { useState, useRef, useEffect } from "react";
import MicButton from "./MicButton";
import PropTypes from 'prop-types';
import { Settings, X, Check, Send, Zap, Info, ChevronDown } from 'lucide-react';
import config from '../config';
import { MentionText } from '../utils/mentions';

const MessageInput = ({
  value,
  onChange,
  onKeyDown,
  onClick,
  isTyping,
  quotedMessage,
  setQuotedMessage,
  setCurrentParentId,
  setInputText,
  AgentSelector,
  analysisMode,
  setAnalysisMode,
}) => {
  const [isMicActive, setIsMicActive] = useState(false);
  // Voice transcript no longer rendered as a separate overlay state;
  // these no-ops keep call sites in MicButton/voice handlers working without changes.
  const setInterimTranscript = () => {};
  const setVoiceText = () => {};
  const [showSettings, setShowSettings] = useState(false);
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const modeDropdownRef = useRef(null);
  const [hasVoiceInput, setHasVoiceInput] = useState(false);
  const [voiceStartIndex, setVoiceStartIndex] = useState(0);
  const speechRecognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const settingsRef = useRef(null);

  // Check if we're in production mode - hide dev controls
  // Returns the active colour classes for an analysis mode pill/button.
  // sky (deep/thinking) and amber (fast) are deliberate semantic signals —
  // kept as fixed Tailwind palette values rather than theme tokens so the
  // meaning is visually distinct from --brand across all themes.
  const analysisModeColors = (mode) => {
    if (mode === "deep") {
      return 'bg-sky-500/20 text-sky-400 border border-sky-500/40';
    }
    return 'bg-amber-500/20 text-amber-400 border border-amber-500/40';
  };
  // Close settings dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target)) {
        setShowModeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-resize textarea when voice text is added
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = newHeight + 'px';
      
      // Auto-scroll to bottom when voice text is being added
      if (hasVoiceInput) {
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
          }
        }, 0);
      }
    }
  }, [value, hasVoiceInput]);

  // Keep the overlay scrolled with the textarea it sits under — the voice
  // transcript layer and the mention-pill layer both depend on it, so this
  // runs in either branch (the textarea element itself is remounted when the
  // branch flips, hence the dependency).
  useEffect(() => {
    if (!textareaRef.current) return;

    const handleScroll = () => {
      if (overlayRef.current && textareaRef.current) {
        overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      }
    };

    const textarea = textareaRef.current;
    handleScroll();
    textarea.addEventListener('scroll', handleScroll);
    
    return () => {
      textarea.removeEventListener('scroll', handleScroll);
    };
  }, [hasVoiceInput]);
  
  const closeQuotedMessage = () => {
    setQuotedMessage(null); // Remove the quoted message
    setCurrentParentId(null);
  };

  const handleTextareaChange = (e) => {
    // Prevent editing if voice input is active
    if (hasVoiceInput) return;
    
    onChange(e);
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  };

  const handleSendMessage = () => {
    onClick();
    // Reset height after sending
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }, 0);
  };

  const handleConfirmVoice = () => {
    // Stop and destroy recognition instance
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.onend = null;
      speechRecognitionRef.current.onresult = null;
      speechRecognitionRef.current.onerror = null;
      speechRecognitionRef.current.onstart = null;
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {
        console.error('Recognition already stopped');
      }
      speechRecognitionRef.current = null; // Destroy instance
    }
    
    // Clear interim transcript
    setInterimTranscript("");
    
    // Merge voice text into regular text (remove highlight)
    setHasVoiceInput(false);
    setVoiceText("");
    setIsMicActive(false);
    setVoiceStartIndex(0);
    
    // Auto-resize textarea after confirming
    if (textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
      }, 0);
    }
  };

  const handleCancelVoice = () => {
    // Stop and destroy recognition instance
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.onend = null;
      speechRecognitionRef.current.onresult = null;
      speechRecognitionRef.current.onerror = null;
      speechRecognitionRef.current.onstart = null;
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {
        console.error('Recognition already stopped');
      }
      speechRecognitionRef.current = null; // Destroy instance
    }
    
    // Remove ALL voice text from input
    setInputText((prevText) => {
      // Remove voice text starting from voiceStartIndex
      const textBeforeVoice = prevText.substring(0, voiceStartIndex);
      return textBeforeVoice;
    });
    
    // Clear all voice-related state
    setInterimTranscript("");
    setVoiceText("");
    setHasVoiceInput(false);
    setIsMicActive(false);
    setVoiceStartIndex(0);
    
    // Auto-resize textarea after canceling
    if (textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
      }, 0);
    }
  };

  return (
    <div className="flex flex-col w-full gap-3">
      {/* Quoted Message */}
      {quotedMessage && (
        <div className="flex items-center p-3 rounded-xl bg-surface border border-line">
          <p className="mb-0 flex-grow">
            <span className="text-xs font-semibold text-fg-muted">
              Replying To:
            </span>
            <br />
            <span className="text-sm text-fg-secondary">
              {quotedMessage}
            </span>
          </p>
          <button
            onClick={closeQuotedMessage}
            className="ml-auto w-7 h-7 rounded-md flex items-center justify-center text-lg border-0 bg-transparent transition-colors text-fg-muted hover:text-fg"
          >
            &times;
          </button>
        </div>
      )}

      {/* Main Input Container - GPT/DeepSeek Style */}
      {/* One row, not two stacked sections: with the agent picker gone the
          bottom row held nothing on its left, so the empty strip under the text
          was pure height. The controls now sit beside the text — items-end
          keeps them pinned to the bottom edge while a long message grows. */}
      <div className="flex items-end rounded-3xl transition-all bg-surface" style={{ boxShadow: 'var(--shadow-lift)' }}>
        {/* Textarea */}
        <div className="min-w-0 flex-1 py-3 pl-4 relative">
          <div className="relative">
            {hasVoiceInput ? (
              <div className="relative w-full">
                <textarea
                  ref={textareaRef}
                  placeholder="Listening..."
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                    } else {
                      onKeyDown?.(e);
                    }
                  }}
                  className="w-full border-0 outline-none resize-none custom-scrollbar transition-colors bg-transparent placeholder:text-fg-muted"
                  style={{
                    height: "auto",
                    minHeight: "24px",
                    maxHeight: "200px",
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: 'transparent',
                    caretColor: 'var(--warning)',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                  rows="1"
                  id="chatInput"
                  readOnly={true}
                />
                <div 
                  ref={overlayRef}
                  className="absolute top-0 left-0 right-0 pointer-events-none whitespace-pre-wrap break-words custom-scrollbar"
                  style={{ 
                    fontSize: '15px',
                    lineHeight: '1.5',
                    maxHeight: '200px',
                    minHeight: '24px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '6px'
                  }}
                >
                  <span className="text-fg">
                    {value.substring(0, voiceStartIndex)}
                  </span>
                  <span className="rounded-sm px-0.5 bg-warning-soft text-warning">
                    {value.substring(voiceStartIndex)}
                  </span>
                </div>
              </div>
            ) : (
              <>
                {/* Mention pills. A textarea cannot style a range of its own
                    text, so the runs are re-rendered in a layer pinned behind
                    it: transparent text, pill backgrounds only. The caret, the
                    selection and IME composition all stay native, and the
                    glyphs on screen are still the textarea's own. The layer
                    copies the field's typography and scrollbar gutter: a
                    pixel of drift there and the pills slide off their words. */}
                <div
                  ref={overlayRef}
                  aria-hidden="true"
                  className="absolute top-0 right-0 pointer-events-none whitespace-pre-wrap break-words custom-scrollbar"
                  // Pulled 4px left and padded 4px back: the content box lands
                  // exactly where the textarea's does (and stays the same width,
                  // scrollbar or not), but the box now has 4px of bleed room for
                  // the round end of a pill that starts at the very first
                  // character, which overflow-x would otherwise shear off.
                  style={{
                    left: '-4px',
                    paddingLeft: '4px',
                    minHeight: '24px',
                    maxHeight: '200px',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    color: 'transparent',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  <MentionText text={value} />
                </div>
                <textarea
                  ref={textareaRef}
                  // Blank on purpose: a native ::placeholder cannot wear a
                  // background-clip gradient, so the shimmering label below stands
                  // in for it while the field is empty. Set a real placeholder here
                  // and both render, which is the doubled text the old screenshots
                  // showed. aria-label keeps the field named for a screen reader,
                  // which never saw the shimmer anyway.
                  placeholder=""
                  aria-label="Type your message"
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (value.trim() !== '') {
                        handleSendMessage();
                      }
                    } else {
                      onKeyDown?.(e);
                    }
                  }}
                  // relative: both this and the pill layer are positioned, so
                  // DOM order decides the paint order and the text lands on top.
                  className="relative w-full bg-transparent border-0 outline-none resize-none custom-scrollbar transition-colors text-fg placeholder:text-fg-muted"
                  style={{ 
                    height: "auto",
                    minHeight: "24px",
                    maxHeight: "200px",
                    fontSize: '15px',
                    lineHeight: '1.5'
                  }}
                  rows="1"
                  id="chatInput"
                />
              </>
            )}
            {/* The waiting label, wearing the same shimmer the status text does —
                one waiting treatment across the app. pointer-events-none so the
                caret lands in the field it floats over. Legible again now that
                the composer's sticky wrapper is opaque: it used to shimmer on top
                of whatever message was scrolling underneath.

                Held 3px off the left edge: a real ::placeholder starts in the
                same pixel column the caret parks in, which put the caret through
                the T. The label is ours to move and the caret is not, so it
                moves. */}
            {!hasVoiceInput && !value && (
              <span
                aria-hidden="true"
                className="shimmer-text pointer-events-none absolute inset-y-0 left-[3px] flex items-center text-[15px]"
              >
                Type your message...
              </span>
            )}
          </div>
        </div>

        {/* Bottom Menu - DeepSeek Style */}
        <div className="flex shrink-0 items-center gap-2.5 py-2 pl-3 pr-3">
          {/* Left Side - Controls */}
          <div className="flex items-center gap-2.5">
            {/* Agent Selector (desktop) */}
            {AgentSelector && (
              <div className="hidden md:flex items-center">
                <AgentSelector />
              </div>
            )}
            {/* Mobile view - Settings dropdown button (always visible) */}
            <div className="md:hidden relative" ref={settingsRef}>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="h-9 px-3 rounded-xl flex items-center justify-center transition-all bg-surface-raised text-fg hover:opacity-80"
                style={{
                  border: '1px solid var(--line)',
                  boxShadow: 'var(--shadow-soft)'
                }}
              >
                <Settings className="w-4 h-4" />
              </button>

              {/* Settings Dropdown */}
              {showSettings && (
                <div className="absolute bottom-full right-0 mb-2 rounded-xl p-3 min-w-[200px] z-50 bg-surface border border-line" style={{ boxShadow: 'var(--shadow-lift)' }}>
                  {/* Agent Selector in dropdown */}
                  {AgentSelector && (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold mb-1.5 text-fg-muted">
                        Agent
                      </label>
                      <AgentSelector />
                    </div>
                  )}
                  {/* Analysis Mode - gated by REACT_APP_FAST_THINKING_MODE_ENABLED */}
                  {config.fastThinkingModeEnabled && (
                    <div>
                      <label className="block text-xs font-semibold mb-1.5 text-fg-muted">
                        Analysis Mode
                      </label>
                      {[
                        { mode: 'fast', icon: <Zap className="w-3.5 h-3.5" />, label: 'Fast', desc: 'Quick, concise responses' },
                        { mode: 'deep', icon: <Info className="w-3.5 h-3.5" />, label: 'Thinking', desc: 'Deeper, step-by-step analysis' },
                      ].map(({ mode, icon, label, desc }) => (
                        <button
                          key={mode}
                          onClick={() => { setAnalysisMode(mode); setShowSettings(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl mb-1 text-left transition-colors ${
                            analysisMode === mode
                              ? analysisModeColors(mode)
                              : 'hover:bg-surface-raised text-fg-secondary'
                          }`}
                        >
                          <span>{icon}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-xs font-semibold">{label}</span>
                            <span className="block text-xs text-fg-muted">{desc}</span>
                          </span>
                          {analysisMode === mode && (
                            <Check className="w-3.5 h-3.5 flex-shrink-0 text-brand" strokeWidth={2.5} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Action Buttons */}
          <div className="flex items-center gap-2.5">
            {hasVoiceInput ? (
              <>
                {/* Recording Indicator */}
                {isMicActive && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  </div>
                )}
                
                {/* Cancel Voice Button (Cross) */}
                <button
                  onClick={handleCancelVoice}
                  aria-label="Cancel voice input"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all bg-surface-raised border border-line text-fg-secondary hover:bg-danger-soft hover:text-danger"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Confirm Voice Button (Tick) */}
                <button
                  onClick={handleConfirmVoice}
                  aria-label="Confirm voice input"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all bg-success-soft border border-line text-success hover:bg-success hover:text-fg-inverse"
                >
                  <Check className="w-5 h-5" strokeWidth={3} />
                </button>
              </>
            ) : (
              <>
                {/* Analysis Mode Pill - desktop only, left of mic */}
                {config.fastThinkingModeEnabled && (
                  <div className="hidden md:block relative" ref={modeDropdownRef}>
                    <button
                      onClick={() => setShowModeDropdown((v) => !v)}
                      className="h-9 flex items-center gap-1 px-3 rounded-full text-xs font-medium transition-all bg-surface-raised text-fg-secondary border border-line hover:opacity-80"
                    >
                      {analysisMode === "deep"
                        ? <Info className="w-3.5 h-3.5 flex-shrink-0" />
                        : <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                      }
                      <span>{analysisMode === "deep" ? "Thinking" : "Fast"}</span>
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${showModeDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showModeDropdown && (
                      <div className="absolute bottom-full right-0 mb-2 rounded-2xl overflow-hidden min-w-[220px] z-50 bg-surface border border-line" style={{ boxShadow: 'var(--shadow-float)' }}>
                        {[
                          { mode: 'fast', icon: <Zap className="w-4 h-4" />, label: 'Fast', desc: 'Quick, concise responses' },
                          { mode: 'deep', icon: <Info className="w-4 h-4" />, label: 'Thinking', desc: 'Deeper, step-by-step analysis' },
                        ].map(({ mode, icon, label, desc }) => (
                          <button
                            key={mode}
                            onClick={() => { setAnalysisMode(mode); setShowModeDropdown(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-raised text-fg"
                          >
                            <span className="text-fg-muted">{icon}</span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-semibold leading-tight">{label}</span>
                              <span className="block text-xs leading-tight mt-0.5 text-fg-muted">{desc}</span>
                            </span>
                            {analysisMode === mode && (
                              <Check className="w-4 h-4 flex-shrink-0 text-brand" strokeWidth={2.5} />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Mic Button */}
                <MicButton
                  isTyping={isTyping}
                  speechRecognitionRef={speechRecognitionRef}
                  setInterimTranscript={setInterimTranscript}
                  setInputText={setInputText}
                  isMicActive={isMicActive}
                  setIsMicActive={setIsMicActive}
                  setVoiceText={setVoiceText}
                  setHasVoiceInput={setHasVoiceInput}
                  setVoiceStartIndex={setVoiceStartIndex}
                  value={value}
                />

                {/* Send Button */}
                <button
                  onClick={handleSendMessage}
                  disabled={isTyping || !value.trim()}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    value.trim() && !isTyping
                      ? 'bg-brand text-brand-fg hover:opacity-90 hover:scale-105 shadow-[0_2px_8px_rgba(14,165,233,0.3)] hover:shadow-[0_4px_12px_rgba(14,165,233,0.4)]'
                      : 'bg-surface-raised text-fg-muted opacity-60 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-4 h-4" fill="currentColor" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Add PropTypes validation

MessageInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onClick: PropTypes.func.isRequired,
  isTyping: PropTypes.bool.isRequired,
  quotedMessage: PropTypes.string,
  setQuotedMessage: PropTypes.func.isRequired,
  setCurrentParentId: PropTypes.func.isRequired,
  setInputText: PropTypes.func.isRequired,
  AgentSelector: PropTypes.elementType,
  analysisMode: PropTypes.oneOf(["deep", "fast"]).isRequired,
  setAnalysisMode: PropTypes.func.isRequired,
};

export default MessageInput;